import React from 'react'
import { Routes, Route, Navigate, Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { Layout } from '../components/Layout'
import { isPwaDisplayMode } from '../hooks/useAppShellMode'
import { InstallApp } from '../pages/InstallApp'
import { LanguageToggle } from '../components/LanguageToggle'

// Import all pages
import { Dashboard } from '../pages/Dashboard'
import { Leads } from '../pages/Leads'
import LeadCreate from '../pages/LeadCreate'
import LeadEdit from '../pages/LeadEdit'
import LeadView from '../pages/LeadView'
import { Customers } from '../pages/Customers'
import CustomerCreate from '../pages/CustomerCreate'
import CustomerEdit from '../pages/CustomerEdit'
import CustomerView from '../pages/CustomerView'
import { Properties } from '../pages/Properties'
import PropertyCreate from '../pages/PropertyCreate'
import PropertyEdit from '../pages/PropertyEdit'
import PropertyView from '../pages/PropertyView'
import { CrewProperties } from '../pages/CrewProperties'
import { CrewPropertyDetail } from '../pages/CrewPropertyDetail'
import { Profile } from '../pages/Profile'
import { Proposals } from '../pages/Proposals'
import ProposalCreate from '../pages/ProposalCreate'
import ProposalEdit from '../pages/ProposalEdit'
import ProposalView from '../pages/ProposalView'
import ProposalRequestCreate from '../pages/ProposalRequestCreate'
import ProposalRequestView from '../pages/ProposalRequestView'
import ProposalRequestEdit from '../pages/ProposalRequestEdit'
import { Contracts } from '../pages/Contracts'
import { ContractCreate } from '../pages/ContractCreate'
import { ContractDetails } from '../pages/ContractDetails'
import { ContractEdit } from '../pages/ContractEdit'
import { Jobs } from '../pages/Jobs'
import { RecurringJobs } from '../pages/RecurringJobs'
import { JobCreate } from '../pages/JobCreate'
import { JobDetails } from '../pages/JobDetails'
import { JobNotes } from '../pages/JobNotes'
import { JobChat } from '../pages/JobChat'
import { JobChecklist } from '../pages/JobChecklist'
import { JobEdit } from '../pages/JobEdit'
import { JobCalendar } from '../pages/JobCalendar'
import { DaySchedule } from '../pages/DaySchedule'
import { TimeClock } from '../pages/TimeClock'
import { Review } from '../pages/Review'
import { QCSchedule } from '../pages/QCSchedule'
import { QCVisits } from '../pages/QCVisits'
import { ReadyToBill } from '../pages/ReadyToBill'
import { Billing } from '../pages/Billing'
import { InvoiceGeneration } from '../pages/InvoiceGeneration'
import { Payments } from '../pages/Payments'
import { QuickBooksSync } from '../pages/QuickBooksSync'
import { ClientPortal } from '../pages/ClientPortal'
import { Reports } from '../pages/Reports'
import { ReportBuilder } from '../pages/ReportBuilder'
import { WorkerSchedule } from '../pages/WorkerSchedule'
import { PnLDashboard } from '../pages/PnLDashboard'
import { Directory } from '../pages/Directory'
import { Messages } from '../pages/Messages'
import { Expenses } from '../pages/Expenses'
import { ExpenseReview } from '../pages/ExpenseReview'
import { QrStickerBatches } from '../pages/QrStickerBatches'
import { EndOfDay } from '../pages/EndOfDay'
import { TimeReview } from '../pages/TimeReview'
import { MessageDetails } from '../pages/MessageDetails'
import { ReviewDetails } from '../pages/ReviewDetails'
import { PublicLeadForm } from '../pages/PublicLeadForm'
import { ServiceCatalog } from '../pages/ServiceCatalog'
import { Settings } from '../pages/Settings'
import { SystemLogs } from '../pages/SystemLogs'
import { CityRecordsSyncLogs } from '../pages/CityRecordsSyncLogs'
import { VisitGroupTemplates } from '../pages/VisitGroupTemplates'
import { BillingMilestoneTemplates } from '../pages/BillingMilestoneTemplates'
import { ChecklistTemplates } from '../pages/ChecklistTemplates'
import { CombinationRules } from '../pages/CombinationRules'
import { TeamManagement } from '../pages/TeamManagement'
import { PlatformTenants } from '../pages/PlatformTenants'
import { PlatformConsole } from '../pages/PlatformConsole'
import { AccessDenied } from '../pages/AccessDenied'
import { CustomerRedirect } from '../components/PermissionGate'
import { PermissionRouteGuard } from '../components/PermissionRouteGuard'

function InboxToMessagesRedirect() {
  const { id } = useParams<{ id: string }>()
  return <Navigate to={id ? `/messages/${id}?tab=external` : '/messages?tab=external'} replace />
}

// Login component
function Login() {
  const { t } = useTranslation(['auth', 'common'])
  const [email, setEmail] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState('')
  const [message, setMessage] = React.useState('')
  const { signIn } = useAuth()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setMessage('')

    try {
      const { error } = await signIn(email, password)

      if (error) {
        if (error.message.includes('Invalid login credentials')) {
          setError(t('auth:invalidCredentials'))
        } else {
          setError(error.message)
        }
      } else {
        setMessage(t('auth:signedInRedirect'))
        setTimeout(async () => {
          try {
            const { data: sess } = await (await import('../lib/supabase')).supabase.auth.getSession()
            const token = sess.session?.access_token
            let dest = '/'
            if (token) {
              const res = await fetch('/api/admin/me/permissions', {
                headers: { Authorization: 'Bearer ' + token },
              })
              if (res.ok) {
                const me = await res.json()
                if (me.isSuperAdmin) dest = '/platform-console'
                else if (isPwaDisplayMode()) {
                  dest = '/dashboard'
                }
              }
            }
            window.location.href = dest
          } catch {
            window.location.href = '/'
          }
        }, 400)
      }
    } catch (err: any) {
      setError(t('auth:signInError', { message: err.message }))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-gradient-to-b from-slate-50 to-blue-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white/90 backdrop-blur rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {t('auth:signInTitle')}
          </h2>
          <div className="mt-4">
            <LanguageToggle compact localOnly />
          </div>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email-address" className="sr-only">
                {t('auth:email')}
              </label>
              <input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none relative block w-full min-h-[48px] px-3.5 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-xl text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={t('auth:email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                {t('auth:password')}
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="appearance-none relative block w-full min-h-[48px] px-3.5 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-xl text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={t('auth:password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          {error && <div className="text-red-600 text-sm text-center">{error}</div>}
          {message && <div className="text-green-600 text-sm text-center">{message}</div>}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center min-h-[48px] py-3 px-4 border border-transparent text-base font-semibold rounded-xl text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? t('auth:signingIn') : t('auth:signIn')}
            </button>
          </div>
        </form>
        {!isPwaDisplayMode() && (
          <p className="text-center text-sm text-gray-500">
            <Link to="/install" className="text-blue-600 hover:text-blue-700 font-medium">
              {t('auth:installApp')}
            </Link>
            <span className="text-gray-400"> · {t('auth:homeScreenAccess')}</span>
          </p>
        )}
      </div>
    </div>
  )
}

// Protected route component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function FieldAppGate({ children }: { children: React.ReactNode }) {
  const { isSuperAdmin, permissionLoading } = useAuth()

  if (permissionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    )
  }

  if (isSuperAdmin) {
    return <Navigate to="/platform-console" replace />
  }

  return <>{children}</>
}

function FieldHomeRedirect() {
  return <Navigate to="/dashboard" replace />
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/install" element={<InstallApp />} />
      <Route path="/quote" element={<PublicLeadForm />} />
      <Route
        path="/platform-console"
        element={
          <ProtectedRoute>
            <PlatformConsole />
          </ProtectedRoute>
        }
      />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <FieldAppGate>
            <CustomerRedirect>
            <Layout>
              <PermissionRouteGuard>
              <Routes>
                <Route path="/" element={<FieldHomeRedirect />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/platform/tenants" element={<PlatformTenants />} />
                <Route path="/access-denied" element={<AccessDenied />} />
                <Route path="/leads" element={<Leads />} />
                <Route path="/leads/create" element={<LeadCreate />} />
                <Route path="/leads/edit/:id" element={<LeadEdit />} />
                <Route path="/leads/view/:id" element={<LeadView />} />
                <Route path="/customers" element={<Customers />} />
                <Route path="/customers/create" element={<CustomerCreate />} />
                <Route path="/customers/edit/:id" element={<CustomerEdit />} />
                <Route path="/customers/view/:id" element={<CustomerView />} />
                <Route path="/my-properties" element={<CrewProperties />} />
                <Route path="/my-properties/:id" element={<CrewPropertyDetail />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/properties" element={<Properties />} />
                <Route path="/properties/create" element={<PropertyCreate />} />
                <Route path="/properties/edit/:id" element={<PropertyEdit />} />
                <Route path="/properties/view/:id" element={<PropertyView />} />
                <Route path="/proposals" element={<Proposals />} />
                <Route path="/proposals/create" element={<ProposalCreate />} />
                <Route path="/proposals/edit/:id" element={<ProposalEdit />} />
                <Route path="/proposals/view/:id" element={<ProposalView />} />
                <Route path="/proposal-requests/create" element={<ProposalRequestCreate />} />
                <Route path="/proposal-requests/view/:id" element={<ProposalRequestView />} />
                <Route path="/proposal-requests/edit/:id" element={<ProposalRequestEdit />} />
                <Route path="/contracts" element={<Contracts />} />
                <Route path="/contracts/create" element={<ContractCreate />} />
                <Route path="/contracts/details/:id" element={<ContractDetails />} />
                <Route path="/contracts/edit/:id" element={<ContractEdit />} />
                <Route path="/jobs" element={<Jobs />} />
                <Route path="/recurring-jobs" element={<RecurringJobs />} />
                <Route path="/jobs/create" element={<JobCreate />} />
                <Route path="/jobs/details/:id/notes" element={<JobNotes />} />
                <Route path="/jobs/details/:id/chat" element={<JobChat />} />
                <Route path="/jobs/details/:id/checklist" element={<JobChecklist />} />
                <Route path="/jobs/details/:id" element={<JobDetails />} />
                <Route path="/jobs/edit/:id" element={<JobEdit />} />
                <Route path="/job-calendar" element={<JobCalendar />} />
                <Route path="/dispatch/:date" element={<DaySchedule />} />
                <Route path="/daily-dispatch" element={<Navigate to="/job-calendar" replace />} />
                <Route path="/time-clock" element={<TimeClock />} />
                <Route path="/my-day" element={<Navigate to="/dashboard" replace />} />
                <Route path="/review" element={<Review />} />
                <Route path="/review/:id" element={<ReviewDetails />} />
                <Route path="/qc-schedule" element={<QCSchedule />} />
                <Route path="/qc-visits" element={<QCVisits />} />
                <Route path="/billing" element={<Billing />} />
                <Route path="/billing/ready" element={<ReadyToBill />} />
                <Route path="/invoice-generation" element={<InvoiceGeneration />} />
                <Route path="/payments" element={<Payments />} />
                <Route path="/quickbooks-sync" element={<QuickBooksSync />} />
                <Route path="/client-portal" element={<ClientPortal />} />
                <Route path="/reports" element={<Reports />} />
                <Route path="/reports/builder" element={<ReportBuilder />} />
                <Route path="/reports/schedule" element={<WorkerSchedule />} />
                <Route path="/pnl-dashboard" element={<PnLDashboard />} />
                <Route path="/directory" element={<Directory />} />
                <Route path="/messages" element={<Messages />} />
                <Route path="/messages/:id" element={<Messages />} />
                <Route path="/inbox" element={<Navigate to="/messages?tab=external" replace />} />
                <Route path="/inbox/:id" element={<InboxToMessagesRedirect />} />
                <Route path="/team-chat" element={<Navigate to="/messages?tab=internal" replace />} />
                <Route path="/expenses" element={<Expenses />} />
                <Route path="/expense-review" element={<ExpenseReview />} />
                <Route path="/activity/:id" element={<MessageDetails />} />
                <Route path="/end-of-day" element={<EndOfDay />} />
                <Route path="/time-review" element={<TimeReview />} />
                <Route path="/service-catalog" element={<ServiceCatalog />} />
                <Route path="/system-logs" element={<SystemLogs />} />
                <Route path="/city-records-sync-logs" element={<CityRecordsSyncLogs />} />
                <Route path="/visit-group-templates" element={<VisitGroupTemplates />} />
                <Route path="/billing-milestone-templates" element={<BillingMilestoneTemplates />} />
                <Route path="/checklist-templates" element={<ChecklistTemplates />} />
                <Route path="/combination-rules" element={<CombinationRules />} />
                <Route path="/settings/users" element={<Navigate to="/settings/team?tab=people" replace />} />
                <Route path="/settings/team" element={<TeamManagement />} />
                <Route path="/settings/qr-stickers" element={<QrStickerBatches />} />
                <Route path="/settings" element={<Settings />} />
              </Routes>
              </PermissionRouteGuard>
            </Layout>
            </CustomerRedirect>
            </FieldAppGate>
          </ProtectedRoute>
        }
      />
    </Routes>
  )
}
